'use client'

import React from 'react'
import {sample, shuffle} from 'lodash'
import {useTranslations} from 'next-intl'
import {css, useTheme} from '@emotion/react'
import {Card} from '@mui/material'

import * as config from './config'
import {GameMode, GameStatus} from './constants'
import {KanaList} from './kanaData'
import Button from '../../../../components/elements/Button'
import useViewportSize from "../../../../hooks/useViewportSize";
import SettingsDialog from './SettingsDialog'
import ProgressBar from './ProgressBar'
import SuccessDialog from './SuccessDialog'
import FailureDialog from './FailureDialog'
import {speakKana} from './utils/speakUtils'

export default function KanaConnect() {
  const theme = useTheme()
  const styles = {
    root: css`
      height: 100%;

      display: flex;
      justify-content: center;
    `,

    container: css`
      background: ${theme.palette.primary.light};
      max-width: 100vw;
      height: 100%;

      display: flex;
      flex-direction: column;
    `,

    header: css`
      background: ${theme.palette.primary.dark};
      
      padding: 16px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;

      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 12px;
      }
    `,

    score: css`
      flex: 1;
      
      padding: 6px 8px;
      
      display: flex;
      gap: 16px;
    `,

    control: css`
      display: flex;
      gap: 4px;
    `,

    content: css`
      flex: 1;

      padding: 16px;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 16px;
      position: relative;

      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 12px;
        gap: 12px;
      }
    `,

    board: css`
      position: relative;
      border: 1px solid #ccc;
    `
  }

  const t = useTranslations()

  const [setting, setSetting] = React.useState({
    mode: GameMode.Hiragana,
    selectedKanaIDs: KanaList.map((k) => k.id)
  })

  // 假設原本想要的內層棋盤大小
  const originalRows = config.Rows
  const originalCols = config.Cols

  // 擴增後的實際大小
  const totalRows = originalRows + 2
  const totalCols = originalCols + 2

  const {width, height, ready} = useViewportSize()
  let cellSize = 60
  if (ready) {
    cellSize = Math.min(cellSize, (width- 32) / totalCols, (height - 32) / totalRows)
    console.log(cellSize, width, totalCols, height, totalRows)
  }

  const [grid, setGrid] = React.useState([])  // grid[r][c]: null | "obstacle" | "card-xxx"
  const [cardsMap, setCardsMap] = React.useState({})
  const [selectedCardId, setSelectedCardId] = React.useState(null)

  const totalTime = config.GameTotalTime
  const [timeLeft, setTimeLeft] = React.useState(totalTime)
  const [score, setScore] = React.useState(0)
  const [gameStatus, setGameStatus] = React.useState(GameStatus.Initial)

  // 路徑動畫
  const [pathState, setPathState] = React.useState({
    coords: [],
    progress: 0,
    visible: false
  })

  // 產生新棋盤
  const generateNewGrid = () => {
    // Initialize empty grid
    const newGrid = Array.from({length: totalRows}, () =>
      Array(totalCols).fill(null)
    )

    // Add random obstacles
    let placedObstacles = 0
    while (placedObstacles < config.ObstacleCount) {
      const r = Math.floor(Math.random() * originalRows) + 1
      const c = Math.floor(Math.random() * originalCols) + 1
      if (newGrid[r][c] === null) {
        newGrid[r][c] = 'obstacle'
        placedObstacles++
      }
    }

    // Find available cells for cards
    let availableCells = []
    for (let r = 1; r <= originalRows; r++) {
      for (let c = 1; c <= originalCols; c++) {
        if (newGrid[r][c] === null) {
          availableCells.push({r, c})
        }
      }
    }
    availableCells = shuffle(availableCells)

    // Calculate number of card pairs to place
    const maxCardsCanPlace = availableCells.length
    const pairCount = Math.floor(maxCardsCanPlace / 2 * config.CardPairsProportion)

    // Select random kana and create pairs
    const availableKanas = KanaList.filter((kana) => setting.selectedKanaIDs.includes(kana.id))
    let chosenKanas = []
    for (let i = 0; i < pairCount; i++) {
      chosenKanas.push(sample(availableKanas))
    }
    chosenKanas = shuffle([...chosenKanas, ...chosenKanas])

    // Place cards on grid
    const newCardsMap = {}
    for (let i = 0; i < chosenKanas.length; i++) {
      const chosenKana = chosenKanas[i]

      const cardId = `card-${i}`
      const {r, c} = availableCells[i]
      newGrid[r][c] = cardId

      let value
      switch (setting.mode) {
        case GameMode.Hiragana:
          value = chosenKana.hiragana
          break
        case GameMode.Katakana:
          value = chosenKana.katakana
          break
        case GameMode.Mixed:
          value = sample([chosenKana.hiragana, chosenKana.katakana])
          break
      }

      newCardsMap[cardId] = {
        id: cardId,
        row: r,
        col: c,
        romaji: chosenKana.romaji,
        value: value,
        color: chosenKana.color,
      }
    }

    return {grid: newGrid, cardsMap: newCardsMap}
  }

  // 初始化遊戲
  const initGame = React.useCallback(() => {
    // 初始化
    setGameStatus(GameStatus.Initial)

    // 設定分數、時間、選取卡等其他狀態
    setTimeLeft(config.GameTotalTime)
    setScore(0)
    setSelectedCardId(null)
    setPathState({coords: [], progress: 0, visible: false})

    // 重新建立新的棋盤
    const {grid, cardsMap} = generateNewGrid()
    setGrid(grid)
    setCardsMap(cardsMap)
  }, [setGameStatus, setTimeLeft, setScore, setSelectedCardId, setPathState, setGrid, setCardsMap])

  // 設定對話框
  const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false)
  const openSettingsDialog = React.useCallback(() => {
    setSettingsDialogOpen(true)
    if (gameStatus === GameStatus.Playing) {
      setGameStatus(GameStatus.Pause)
    }
  }, [gameStatus, setSettingsDialogOpen, setGameStatus])

  const closeSettingsDialog = React.useCallback(() => {
    setSettingsDialogOpen(false)
    if (gameStatus === GameStatus.Pause) {
      setGameStatus(GameStatus.Playing)
    }
  }, [gameStatus, setSettingsDialogOpen, setGameStatus])

  const applySettings = React.useCallback((newSetting) => {
    setSetting(newSetting)
    initGame()
    setGameStatus(GameStatus.Playing)

    setSettingsDialogOpen(false)
  }, [setSetting, initGame, setGameStatus])

  // 遊戲結束對話框
  const [gameOverDialogOpen, setGameOverDialogOpen] = React.useState(false)

  const closeGameOverDialogDialog = React.useCallback(() => {
    setGameOverDialogOpen(false)
  }, [setGameOverDialogOpen])

  // 首次載入
  React.useEffect(() => {
    initGame()
  }, [])

  // 成功時的彈窗
  const handleGameSuccess = () => {
    setGameStatus(GameStatus.Success)
    setGameOverDialogOpen(true)
  }

  // 失敗時的彈窗
  const handleGameFailure = () => {
    setGameStatus(GameStatus.Failure)
    setGameOverDialogOpen(true)
  }

  // 啟動遊戲
  const startGame = React.useCallback(() => {
    initGame()
    setGameStatus(GameStatus.Playing)
  }, [initGame, setGameStatus])

  // 重啟遊戲
  const restartGame = React.useCallback(() => {
    setGameOverDialogOpen(false)
    startGame()
  }, [setGameOverDialogOpen, startGame])

  // 倒數計時
  React.useEffect(() => {
    if (gameStatus === GameStatus.Playing) {
      const timer = setInterval(() => {
        setTimeLeft((t) => t - 1)
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [gameStatus])

  // 時間到
  React.useEffect(() => {
    if (gameStatus === GameStatus.Playing) {
      if (timeLeft <= 0) {
        handleGameFailure()
      }
    }
  }, [gameStatus, timeLeft])

  // 全部消除
  React.useEffect(() => {
    if (gameStatus === GameStatus.Playing) {
      if (Object.keys(cardsMap).length === 0 && timeLeft > 0) {
        handleGameSuccess()
      }
    }
  }, [gameStatus, cardsMap, timeLeft])

  // 消除兩張卡
  const removeCards = React.useCallback((cardIdA, cardIdB) => {
    setGrid((prev) => {
      const newGrid = prev.map((rowArr) => [...rowArr])
      const cA = cardsMap[cardIdA]
      const cB = cardsMap[cardIdB]
      if (cA) newGrid[cA.row][cA.col] = null
      if (cB) newGrid[cB.row][cB.col] = null
      return newGrid
    })
    setCardsMap((prev) => {
      const newMap = {...prev}
      delete newMap[cardIdA]
      delete newMap[cardIdB]
      return newMap
    })
  }, [setGrid, cardsMap, setCardsMap])

  // 動畫顯示路徑
  const animatePath = React.useCallback((coords, onDone) => {
    setPathState({
      coords,
      progress: 0,
      visible: true
    })

    let startTime = null
    const duration = 300

    function step(timestamp) {
      if (!startTime) startTime = timestamp
      const elapsed = timestamp - startTime
      const t = Math.min(elapsed / duration, 1)

      setPathState((prev) => ({
        ...prev,
        progress: t
      }))

      if (t < 1) {
        requestAnimationFrame(step)
      } else {
        // 動畫結束
        setTimeout(() => {
          setPathState({coords: [], progress: 0, visible: false})
          onDone?.()
        }, 100)
      }
    }

    requestAnimationFrame(step)
  }, [setPathState])

  // 點擊卡片
  const handleCardClick = React.useCallback((cardId) => {
    if (!cardsMap[cardId]) {
      return
    }

    const card = cardsMap[cardId]

    // 發音
    speakKana(card.value, {
      pitch: 1.2,  // 稍微提高音調使發音更清晰
    })

    // 若尚未選取卡，直接選取
    if (!selectedCardId) {
      setSelectedCardId(cardId)
      return
    }

    // 已選取一張 => 檢查是否同假名
    if (selectedCardId === cardId) {
      // 點到同一張 => 取消選取
      setSelectedCardId(null)
      return
    }

    const firstCard = cardsMap[selectedCardId]
    if (firstCard.value === card.value) {
      // 找路徑
      const path = findPathBetweenTwoCards(grid, firstCard, card, cellSize, originalRows, originalCols)
      if (path) {
        // 成功 => 顯示連線動畫 => 消除卡片
        animatePath(path, () => {
          removeCards(firstCard.id, card.id)
          setScore((s) => s + config.ScorePerMatch)
        })
      }
      setSelectedCardId(null)
    } else {
      // 不同假名 => 換選新卡
      setSelectedCardId(cardId)
    }
  }, [cardsMap, selectedCardId, setSelectedCardId, removeCards, setScore, animatePath, cellSize, originalRows, originalCols, grid])

  // 計算 polyline
  const polyPoints = React.useMemo(() => {
    let polyPoints = ''
    if (pathState.visible && pathState.coords.length > 1) {
      const total = pathState.coords.length - 1
      const maxIndex = Math.floor(pathState.progress * total)
      const partial = pathState.coords.slice(0, maxIndex + 1)

      // 部分補內插
      if (maxIndex < total) {
        const ratio = pathState.progress * total - maxIndex
        const start = pathState.coords[maxIndex]
        const end = pathState.coords[maxIndex + 1]
        const x = start.x + (end.x - start.x) * ratio
        const y = start.y + (end.y - start.y) * ratio
        partial.push({x, y})
      }
      polyPoints = partial.map((p) => `${p.x},${p.y}`).join(' ')
    }

    return polyPoints
  }, [pathState.visible, pathState.progress, pathState.coords])

  return (
    <div css={styles.root}>
      <Card css={styles.container}>
        <Card css={styles.header}>
          <Card css={styles.score}>
            <span>{t('kana-connect.game-score-label' )} {score}</span>
          </Card>
          <div css={styles.control}>
            <Button onClick={openSettingsDialog}>{t('kana-connect.settings-action')}</Button>
            <Button onClick={startGame}>
              {gameStatus === GameStatus.Initial ? t('kana-connect.start-action') : t('kana-connect.restart-action')}
            </Button>
          </div>
        </Card>
        <div css={styles.content}>
          {/* 進度條 */}
          <ProgressBar timeLeft={timeLeft} totalTime={totalTime}/>

          <div css={css`flex: 1;`}>
            <Card
              css={styles.board}
              style={{
                width: totalCols * cellSize,
                height: totalRows * cellSize
              }}
            >
              {/* 路徑動畫層 */}
              {pathState.visible && (
                <svg
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none'
                  }}
                >
                  <polyline
                    points={polyPoints}
                    fill="none"
                    stroke="red"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              )}

              {/* 繪製格子 */}
              {grid.map((rowArr, r) =>
                rowArr.map((cellValue, c) => {
                  const card = typeof cellValue === 'string' && cellValue.startsWith('card-')
                    ? cardsMap[cellValue]
                    : null

                  let bg = ''
                  let content = ''
                  let cursor = 'default'

                  if (cellValue === 'obstacle') {
                    bg = '#666'
                  } else if (card) {
                    bg = card.color
                    content = (
                      <>
                        <div style={{fontSize: 18, fontWeight: 'bold'}}>
                          {card.value}
                        </div>
                      </>
                    )
                    cursor = 'pointer'
                  }

                  const isSelected = card && card.id === selectedCardId

                  return (
                    <div
                      key={`${r}-${c}`}
                      onClick={() => card && handleCardClick(card.id)}
                      style={{
                        position: 'absolute',
                        left: c * cellSize,
                        top: r * cellSize,
                        width: cellSize,
                        height: cellSize,
                        border: '1px solid #ddd',
                        boxSizing: 'border-box',
                        backgroundColor: bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s',
                        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                        cursor
                      }}
                    >
                      {content}
                    </div>
                  )
                })
              )}
            </Card>
          </div>
        </div>
      </Card>

      <SettingsDialog
        isOpen={settingsDialogOpen}
        setting={setting}
        onClose={closeSettingsDialog}
        onApply={applySettings}
      />

      <SuccessDialog
        isOpen={gameOverDialogOpen && gameStatus === GameStatus.Success}
        onClose={closeGameOverDialogDialog}
        onRestart={restartGame}
        score={score}
        timeLeft={timeLeft}
      />

      <FailureDialog
        isOpen={gameOverDialogOpen && gameStatus === GameStatus.Failure}
        onClose={closeGameOverDialogDialog}
        onRestart={restartGame}
        score={score}
      />
    </div>
  )
}


// BFS 找路徑
function findPathBetweenTwoCards(grid, cardA, cardB, cellSize, rows, cols) {
  // 建立副本，不修改原 grid
  const tempGrid = grid.map((row) => [...row])

  // 暫時把 A, B 設為 null
  tempGrid[cardA.row][cardA.col] = null
  tempGrid[cardB.row][cardB.col] = null

  // 執行 BFS
  const path = bfsFindPathWithBends(tempGrid, {r: cardA.row, c: cardA.col}, {
    r: cardB.row,
    c: cardB.col
  }, rows, cols)

  if (!path) {
    return null
  }

  // 計算某格子中心在畫面中的 (x, y) 座標
  // result => [{ r, c }, { r, c }, ...]
  // 轉成 (x, y)
  return path.map((pos) => ({
    x: pos.c * cellSize + cellSize / 2,
    y: pos.r * cellSize + cellSize / 2
  }))
}


/**
 * BFS：可在擴增後的 (rows+2)×(cols+2) 范圍內走動，最多轉兩次 90 度
 * @param {Array} grid - 二維陣列 (rows+2, cols+2)
 * @param {{r,c}} start - 起點 (含外圈)
 * @param {{r,c}} end   - 終點 (含外圈)
 * @param {Number} rows - 實際使用的「擴增前」高度 (若需要)
 * @param {Number} cols - 實際使用的「擴增前」寬度 (若需要)
 * 回傳：找到路 => [{r,c}...], 否則 null
 */
function bfsFindPathWithBends(grid, start, end, rows, cols) {
  // 建議：rows + 2 / cols + 2 才是 grid 的實際大小
  const totalRows = rows + 2
  const totalCols = cols + 2

  // 若起終點相同 => 直接回傳空
  if (start.r === end.r && start.c === end.c) return []

  // visited[r][c][dir][bends] => 避免重複拜訪
  const visited = {}

  // 初始佇列
  const queue = []
  // dir = -1 表示尚未確定方向, bends=0
  queue.push({
    r: start.r,
    c: start.c,
    dir: -1,
    bends: 0,
    path: [{r: start.r, c: start.c}]
  })
  visited[keyOf(start.r, start.c, -1, 0)] = true

  // 定義方向 (上, 下, 左, 右)，可自訂 0~3
  const directions = [
    {dr: -1, dc: 0, dirId: 0}, // up
    {dr: 1, dc: 0, dirId: 1},  // down
    {dr: 0, dc: -1, dirId: 2}, // left
    {dr: 0, dc: 1, dirId: 3}  // right
  ]

  while (queue.length > 0) {
    const curr = queue.shift()
    // 抵達目標
    if (curr.r === end.r && curr.c === end.c) {
      return curr.path
    }

    for (let {dr, dc, dirId} of directions) {
      const nr = curr.r + dr
      const nc = curr.c + dc

      // 邊界檢查: 0<=nr<totalRows & 0<=nc<totalCols
      if (nr < 0 || nr >= totalRows) continue
      if (nc < 0 || nc >= totalCols) continue

      // 計算轉彎數
      let newBends = curr.bends
      if (curr.dir !== -1 && curr.dir !== dirId) {
        newBends += 1
      }
      if (newBends > 2) {
        // 超過2次彎 => 不可走
        continue
      }

      // 檢查該位置是否可走
      if (grid[nr][nc] !== null) {
        // 有卡片、障礙或其他 => 不可走
        continue
      }

      const key = keyOf(nr, nc, dirId, newBends)
      if (visited[key]) continue

      visited[key] = true
      queue.push({
        r: nr,
        c: nc,
        dir: dirId,
        bends: newBends,
        path: [...curr.path, {r: nr, c: nc}]
      })
    }
  }

  // 沒找到
  return null
}

function keyOf(r, c, d, b) {
  return `${r},${c},${d},${b}`
}

